// Tandem AI Inference System — runs dual-agent conversations on a single inference core.
//
// Tandem's conversation is split across two internal `Chat` instances:
//   - `chatInstructor` — what the instructor AI sees (user + instructor assistant + tagged
//      engineer-to-instructor messages arrive here as "user" segments)
//   - `chatEngineer`   — what the engineer AI sees (instructor-to-engineer messages arrive
//      here as "user" segments)
//
// Because inference is single-flight per system, we swap `this.chat` (inherited from
// `AIInferenceSystem`) between the two chats by calling `setActiveLane('instructor'|'engineer')`
// before each infer / message-add cycle. This keeps every feature inherited from
// `AIInferenceSystem` (openrouter messaging, local llama format, checkpoints, instanceToolData)
// working unchanged while still letting us maintain two independent model perspectives.
//
// Each `Chat` is seeded with its own `role: "system"` message on construction and whenever
// `newChat()` runs (see `initializeTandemChatsWithSystemPrompts`).

import { AIInferenceSystem } from "../../../base/AIInferenceSystem.js";
import { Chat } from "../../../chat/Chat.js";

export const TANDEM_LANES = Object.freeze({
  INSTRUCTOR: "instructor",
  ENGINEER: "engineer",
});

const DEFAULT_INSTRUCTOR_SYSTEM_PROMPT =
  "You are the Instructor in Tandem — the **architect and instructor** between the human user " +
  "and a separate **Engineer** model.\n" +
  "\n" +
  "**Roles:** You plan, clarify goals, summarize progress for the human, and decide *what* should " +
  "happen next. The Engineer is the **hands-on implementer**: it alone may use tools to read files, " +
  "write files, search the project, and produce concrete artifacts. Default to **routing " +
  "implementation and tool-heavy work through the Engineer** instead of answering as if you " +
  "personally ran those tools. When the human asks for code, files, repo exploration, or " +
  "rigorous build-out, involve the Engineer unless they clearly want only a brief conceptual reply " +
  "with no further execution.\n" +
  "\n" +
  "**User messages are steering signals:** Anything the human sends here—especially text prefixed " +
  "`[user]`—is an instruction for how you should **drive Tandem forward**, usually by **using the " +
  "Engineer**. Do not treat their messages as an invitation to be a standalone chatbot that " +
  "ignores the Engineer whenever work could be delegated.\n" +
  "\n" +
  "**No direct human ↔ Engineer link:** The Engineer never sees the human and has no idea another " +
  "person exists. In the Engineer’s chat, **you are the user** — write handoffs so the Engineer " +
  "believes it is helping a single human stakeholder. Never mention “Tandem”, dual agents, hidden " +
  "humans, or that instructions came from elsewhere. You are the only bridge: the human talks to " +
  "you; you talk to the Engineer as “the user”; the Engineer’s results come back to you as " +
  "structured summaries for you to interpret and act on.\n" +
  "\n" +
  "**Reading engineer output:** Engineer work appears here as an `[Engineer]` line plus a markdown " +
  "fenced block labeled `tandem-handoff` with XML-like sections (`<Thinking>`, `<Response>`, " +
  "`<Tool Call>` with `id:` then `tool-name(args)`, `<Tool Result>` with `id:` then payload). " +
  "The fence prevents the UI from stripping tags; treat the fenced body as the canonical transcript.\n" +
  "\n" +
  "**Orchestration tool (only when delegating):** The only special tool in this lane is " +
  "`tandem-prompt-engineer` with `instructionsForEngineer`. Call it **when you need another " +
  "Engineer pass** (implementation, repo work, tool-backed analysis). It halts after the tool batch " +
  "so the system can run the Engineer lane safely.\n" +
  "\n" +
  "**Talking to the human:** When you are only replying to the user in this instructor chat, use " +
  "**normal assistant messages** (and reasoning segments) like any assistant — **do not** use " +
  "`tandem-prompt-engineer` for pure conversational answers that do not require the Engineer.\n" +
  "\n" +
  "Stay concise unless depth is needed. When you delegate, give the Engineer crisp, actionable " +
  "instructions the same way a senior engineer would brief a trusted implementer.";

const DEFAULT_ENGINEER_SYSTEM_PROMPT =
  "You are a capable software and reasoning assistant.\n" +
  "\n" +
  "The user you chat with may be a human or may paste instructions on behalf of another party; " +
  "treat every user message as authoritative context and respond helpfully.\n" +
  "\n" +
  "Use tools when they help complete the task. Explain non-trivial choices briefly. " +
  "Do not assume you are part of a special “dual agent” setup unless the user says so.";

export class TandemAIInferenceSystem extends AIInferenceSystem {
  constructor() {
    super();

    /** Section manager reference (set via connectInstanceSystems → setWorkspaceManager). */
    this.workspaceManager = null;

    /** Per-lane Chat instances. `super()` already created `this.chat`; repurpose it as engineer. */
    this.chatEngineer = this.chat;
    this.chatInstructor = new Chat();

    /** @type {"instructor" | "engineer"} */
    this.activeLane = TANDEM_LANES.ENGINEER;

    this.initializeTandemChatsWithSystemPrompts();
  }

  getInstanceId() {
    return "tandem";
  }

  /**
   * @param {*} manager — section `TandemManager`
   */
  setWorkspaceManager(manager) {
    this.workspaceManager = manager;
  }

  /**
   * Instructor lane system text (OpenRouter messages + local `systemPrompt` via `Chat` conversion).
   * Override in a subclass if a section needs custom copy.
   * @returns {string}
   */
  getInstructorSystemPrompt() {
    return DEFAULT_INSTRUCTOR_SYSTEM_PROMPT;
  }

  /**
   * Engineer lane system text.
   * @returns {string}
   */
  getEngineerSystemPrompt() {
    return DEFAULT_ENGINEER_SYSTEM_PROMPT;
  }

  /**
   * Inserts the initial `role: "system"` row on **both** lane chats. Call after every time
   * `chatInstructor` / `chatEngineer` are replaced with fresh `Chat` instances (`newChat`,
   * or construction). Safe on empty chats only.
   */
  initializeTandemChatsWithSystemPrompts() {
    if (!this.chatInstructor || !this.chatEngineer) {
      return;
    }

    const seedIfEmpty = (chat, text) => {
      if (chat.getMessageCount?.() > 0) {
        return;
      }
      chat.addMessage({
        role: "system",
        content: text,
      });
    };

    seedIfEmpty(this.chatInstructor, this.getInstructorSystemPrompt());
    seedIfEmpty(this.chatEngineer, this.getEngineerSystemPrompt());
  }

  /**
   * Swap the active Chat used by inference/tool helpers. Must be called while the
   * system is idle (`!this.isInferring`) — callers should always set the lane
   * before `addUserMessage` / `infer`, never during streaming.
   * @param {"instructor" | "engineer"} lane
   */
  setActiveLane(lane) {
    if (lane !== TANDEM_LANES.INSTRUCTOR && lane !== TANDEM_LANES.ENGINEER) {
      return;
    }
    if (this.activeLane === lane) {
      return;
    }
    this.activeLane = lane;
    this.chat =
      lane === TANDEM_LANES.INSTRUCTOR ? this.chatInstructor : this.chatEngineer;
  }

  /** @returns {"instructor" | "engineer"} */
  getActiveLane() {
    return this.activeLane;
  }

  /**
   * Convenience accessor for cross-lane tools / UIs.
   * @param {"instructor" | "engineer"} lane
   */
  getChatForLane(lane) {
    if (lane === TANDEM_LANES.INSTRUCTOR) return this.chatInstructor;
    if (lane === TANDEM_LANES.ENGINEER) return this.chatEngineer;
    return null;
  }

  /**
   * Reset *both* chats when starting a new tandem session. Preserves the original
   * `newChat()` contract (base emits `onChatModified('newChat')` for the active one).
   */
  newChat() {
    this.chatEngineer = new Chat();
    this.chatInstructor = new Chat();
    this.chat =
      this.activeLane === TANDEM_LANES.INSTRUCTOR
        ? this.chatInstructor
        : this.chatEngineer;
    this.initializeTandemChatsWithSystemPrompts();
    this.onChatModified("newChat");
  }

  /**
   * Persist both lane chats to Revolt Desktop (`tandem-projects.json`) when a project
   * and session id are active — see {@link TandemManager.schedulePersistTandemSession}.
   * @param {string} operation
   * @param {Object} data
   */
  onChatModified(operation, data = {}) {
    void operation;
    void data;
    const mgr = this.workspaceManager;
    if (mgr && typeof mgr.schedulePersistTandemSession === "function") {
      mgr.schedulePersistTandemSession();
    }
  }
}
